// Vista "home": grilla con todas las empresas (o solo las asignadas, si el
// usuario tiene rol "empresa"). Permite crear, entrar y (admin) eliminar.
import { useState, useEffect, useRef } from "react";
import { Building2, Plus, Trash2, ArrowRight, Loader, AlertCircle, FileText, Calendar } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import CompanyModal from "./CompanyModal";
import { useConfirm } from "./ConfirmDialog";
import { formatDate } from "../utils/dates";
import { SkeletonGrid } from "./Skeletons";

// Color determinístico por nombre de empresa (mismo hash que folderColor en constants.js)
const SECTOR_COLORS = [
  "#00A896", "#7C3AED", "#059669", "#D97706", "#DC2626",
  "#0891B2", "#C026D3", "#65A30D", "#EA580C", "#2563EB",
];

function sectorColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return SECTOR_COLORS[Math.abs(h) % SECTOR_COLORS.length];
}

function initials(name) {
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

function CompanyAvatar({ company, color, canUpload, onUploaded }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e, file) => {
    e.stopPropagation();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const updated = await api.uploadCompanyLogo(company.id, fd);
      if (updated.error) throw new Error(updated.error);
      onUploaded(updated);
    } catch (err) {
      alert("No se pudo subir el logo: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="company-avatar-lg"
      onClick={e => { if (canUpload) { e.stopPropagation(); fileRef.current.click(); } }}
      title={canUpload ? "Cambiar logo" : undefined}
      style={{
        background: company.logo ? `url(${api.fileUrl(company.logo)}) center/cover` : color,
        cursor: canUpload ? "pointer" : "default",
        overflow: "hidden",
      }}>
      {canUpload && (
        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" hidden
          onClick={e => e.stopPropagation()}
          onChange={e => handleFile(e, e.target.files[0])} />
      )}
      {uploading ? <Loader size={16} className="spin" /> : (!company.logo && initials(company.name))}
    </div>
  );
}

export default function CompanyGrid({ search, onEnter }) {
  const { canDelete, canManageCompanies, isEmpresa, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { confirm, ConfirmUI }    = useConfirm();

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
      setError(null);
    } catch {
      setError("No se pudo conectar al servidor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e, company) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Eliminar empresa",
      message: `"${company.name}" y todos sus documentos serán eliminados permanentemente.`,
      confirmLabel: "Eliminar todo",
    });
    if (!ok) return;
    await api.deleteCompany(company.id);
    setCompanies(prev => prev.filter(c => c.id !== company.id));
  };

  // Un usuario de rol "empresa" solo debe ver las empresas que tiene asignadas
  // (el backend ya filtra el detalle, pero aquí también se filtra la lista general)
  const visibleCompanies = isEmpresa
    ? companies.filter(c => user?.company_ids?.includes(c.id))
    : companies;

  const filtered = visibleCompanies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.nit || "").includes(search) ||
    (c.sector || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <SkeletonGrid variant="company" count={6} />;

  if (error) return (
    <div className="empty-state">
      <AlertCircle size={40} style={{ color: "#E53E3E", opacity: 0.7 }} />
      <p style={{ color: "#E53E3E" }}>{error}</p>
    </div>
  );

  return (
    <>
      <div className="company-toolbar">
        <p className="company-count">
          {filtered.length} empresa{filtered.length !== 1 ? "s" : ""}
        </p>
        {canManageCompanies && (
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} /> Nueva empresa
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <Building2 size={48} opacity={0.3} />
          <p>{search ? "Sin resultados para tu búsqueda." : "Aún no hay empresas registradas."}</p>
          {!search && (
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={15} /> Crear primera empresa
            </button>
          )}
        </div>
      ) : (
        <div className="company-grid">
          {filtered.map(company => {
            const color = sectorColor(company.name);
            return (
              <div key={company.id} className="company-card" onClick={() => onEnter(company)}>
                <div className="company-card-top">
                  <CompanyAvatar
                    company={company}
                    color={color}
                    canUpload={canManageCompanies}
                    onUploaded={(updated) => setCompanies(prev => prev.map(c => c.id === updated.id ? updated : c))}
                  />
                  {canDelete && (
                    <button className="action-btn danger company-delete"
                      onClick={(e) => handleDelete(e, company)}
                      title="Eliminar empresa">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                <div className="company-card-body">
                  <h3 className="company-name">{company.name}</h3>
                  {company.nit && <p className="company-meta">NIT: {company.nit}</p>}
                  {company.sector && <p className="company-sector">{company.sector}</p>}
                  {company.city && <p className="company-meta">{company.city}</p>}
                </div>

                <div className="company-card-footer">
                  <span style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--text-muted)", fontSize: "0.72rem" }}>
                    <FileText size={13} /> {company.doc_count ?? 0} docs
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--text-muted)", fontSize: "0.72rem" }}>
                    <Calendar size={13} /> {formatDate(company.created_at)}
                  </span>
                </div>

                <div className="company-card-enter">
                  Ver documentos <ArrowRight size={14} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CompanyModal
          onClose={() => setShowModal(false)}
          onCreated={(c) => setCompanies(prev => [c, ...prev])}
        />
      )}
      {ConfirmUI}
    </>
  );
}
